'use client';

/**
 * Imperative "why are you deleting this?" dialog.
 *
 * Same promise-based shape as `confirmDialog`, but it resolves to the REASON
 * string instead of a boolean — null means the user cancelled:
 *
 *   const reason = await deleteReasonDialog({
 *     title: 'Delete agent',
 *     message: `"${name}" and its backing workflow will be removed.`,
 *     resource: 'agent',
 *   });
 *   if (!reason) return;
 *   await service.delete(id, reason);
 *
 * Built as its own dialog rather than a flag on `confirmDialog`, because
 * AppConfirmDialog is a confirmation primitive shared by a lot of non-delete
 * callers and threading an input through it would complicate all of them for
 * the benefit of a few.
 *
 * Surfaces whose delete dialog ALREADY has a reason field (workspaces,
 * projects, workflows, connectors) keep theirs and validate inline with
 * `validateDeleteReason` — they carry extra chrome (type-to-confirm, cascade
 * warnings, resource pickers) this dialog deliberately does not try to absorb.
 *
 * Mount <DeleteReasonDialogHost /> once near the app root.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { validateDeleteReason } from '@/lib/delete-reason';

export interface DeleteReasonOptions {
  title?: string;
  /** What is about to happen. Shown above the field. */
  message: string;
  /** Noun used in the label and placeholder, e.g. 'agent', 'knowledgebase'. */
  resource?: string;
  confirmText?: string;
}

type Resolver = (reason: string | null) => void;
let _open: ((opts: DeleteReasonOptions) => Promise<string | null>) | null = null;

/** Resolves to the trimmed reason, or null if the user cancelled. */
export function deleteReasonDialog(opts: DeleteReasonOptions): Promise<string | null> {
  if (_open) return _open(opts);
  // No host mounted — refuse rather than delete without a reason. Silently
  // resolving to a placeholder string would defeat the whole rule.
  return Promise.resolve(null);
}

export function DeleteReasonDialogHost() {
  const [opts, setOpts] = useState<DeleteReasonOptions | null>(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const resolverRef = useRef<Resolver | null>(null);

  useEffect(() => {
    _open = (o: DeleteReasonOptions) =>
      new Promise<string | null>((resolve) => {
        resolverRef.current = resolve;
        setReason('');
        setTouched(false);
        setOpts(o);
      });
    return () => { _open = null; };
  }, []);

  const settle = useCallback((value: string | null) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    resolve?.(value);
  }, []);

  const error = validateDeleteReason(reason);

  const submit = useCallback(() => {
    if (error) {
      // Reveal the message rather than doing nothing — a dead button leaves
      // the user guessing why the dialog will not close.
      setTouched(true);
      return;
    }
    settle(reason.trim());
  }, [error, reason, settle]);

  if (!opts) return null;

  const resource = opts.resource ?? 'item';
  const showError = touched && !!error;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) settle(null); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{opts.title ?? 'Confirm deletion'}</DialogTitle>
        </DialogHeader>

        <div className="py-2 text-[13px] text-text-secondary">{opts.message}</div>

        <div className="space-y-1.5">
          <label htmlFor="delete-reason-input" className="block text-sm font-medium text-text-primary">
            Reason for Deletion <span className="text-red-500">*</span>
          </label>
          <textarea
            id="delete-reason-input"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={`Enter why this ${resource} is being deleted`}
            rows={4}
            aria-invalid={showError}
            aria-describedby={showError ? 'delete-reason-error' : undefined}
            className={`w-full resize-none rounded-md border bg-white px-3 py-2 text-[13px] text-gray-900 outline-none focus:ring-2 dark:bg-gray-800 dark:text-white ${
              showError
                ? 'border-red-500 focus:ring-red-500/40'
                : 'border-gray-300 focus:ring-blue-500/40 dark:border-gray-600'
            }`}
          />
          {showError && (
            <p id="delete-reason-error" role="alert" className="text-[12px] text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => settle(null)}>Cancel</Button>
          <Button
            onClick={submit}
            // Not `disabled`: a disabled button cannot be clicked, so it can
            // never explain itself. Clicking with an empty field shows why.
            aria-disabled={!!error}
            className={`bg-red-600 text-white hover:bg-red-700 ${error ? 'opacity-60' : ''}`}
          >
            {opts.confirmText ?? 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteReasonDialogHost;
